import React, { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { HubButton } from "../inputs";
import { HubContextMenu } from "../HubContextMenu";
import { HubIcon } from "../HubIcon";

import { StyledTypography, StyledStack } from "./styles";
import { HubHookFormSwitch } from "../HubHookFormSwitch";

export const HubFilterMenu = ({
  id = "filter",
  options = [],
  menuLabel = "",
  label = "",
  icon,
  joinBy = "-",
  multiSelect = true,
  onChange = () => {},
  useDefault = true,
  defaultValue,
  buttonProps = {},
  searchable = false,
  searchPlaceholder = "Search…",
}) => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const property = query.get(id);

  const first = multiSelect ? [options[0]?.id] : options[0]?.id;
  const startValue = useDefault ? defaultValue || first : [];
  const [filters, setFilters] = useState(startValue);

  useEffect(() => {
    if (!useDefault) return;
    if (multiSelect) {
      if (property === "All") {
        const filtered = options?.map((o) => Number(o?.id));
        setFilters(filtered);
      } else {
        let data = [];
        if (property) data = property?.split(joinBy);
        else if (defaultValue) data = defaultValue;
        else data = [options[0]?.id];

        data = data?.map(Number);

        const optionIds = options?.map((o) => Number(o?.id));
        const filtered = data?.filter((i) => optionIds?.includes(i));

        setFilters(filtered);
      }
    } else {
      const selected = property || defaultValue || options[0]?.id;
      setFilters(selected);
    }
  }, []);

  useEffect(() => {
    if (multiSelect) {
      if (!filters?.length) query.delete(id);
      else if (filters.length === options.length) query.set(id, "All");
      else query.set(id, filters?.join(joinBy));
    } else {
      if (!filters) query.delete(id);
      else query.set(id, filters);
    }

    navigate({
      pathname: location.pathname,
      search: query.toString(),
    });

    onChange();
  }, [filters]);

  const handleSwitchClick = (id) => {
    if (multiSelect) {
      filters?.includes(id)
        ? setFilters(filters?.filter((f) => f !== id))
        : setFilters([...filters, id]);
    } else {
      filters === id ? setFilters() : setFilters(id);
    }
  };

  const handleOnlyClick = (id) => {
    setFilters(multiSelect ? [id] : id);
  };

  const visibleOptions = useMemo(() => {
    if (!searchable || !search) return options;
    const needle = search.toLowerCase();
    return (options || []).filter((o) =>
      String(o?.label ?? "")
        .toLowerCase()
        .includes(needle),
    );
  }, [options, search, searchable]);

  const MenuOptions = (
    <Stack gap="10px" padding="15px" sx={{ maxHeight: 360, overflow: "auto" }}>
      {searchable && (
        <OutlinedInput
          size="small"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          startAdornment={
            <InputAdornment position="start">
              <HubIcon icon="Search" size="12px" />
            </InputAdornment>
          }
          sx={{ fontSize: 12, padding: "8px 10px" }}
        />
      )}
      {menuLabel && <Typography variant="caption">{menuLabel}</Typography>}
      {visibleOptions?.length === 0 && (
        <Typography variant="caption11" color="text.secondary">
          No matches
        </Typography>
      )}
      {visibleOptions?.map((l) => (
        <StyledStack key={l?.id}>
          <Stack direction="row" alignItems="center" gap="10px">
            <Checkbox
              key={l?.id}
              onChange={() => handleSwitchClick(l?.id)}
              checked={
                multiSelect ? filters?.includes(l?.id) : filters === l?.id
              }
              sx={{ padding: 0 }}
            />
            <Typography variant="caption11">{l?.label}</Typography>
          </Stack>
          {multiSelect && (
            <StyledTypography
              id="OnlyButton"
              onClick={() => handleOnlyClick(l?.id)}
            >
              Only
            </StyledTypography>
          )}
        </StyledStack>
      ))}
    </Stack>
  );

  const length = filters?.length ? `(${filters?.length})` : "";
  const num = filters ? "(1)" : "";
  const filterNum = multiSelect ? length : num;

  const MenuButton = (
    <HubButton
      variant="tinyButton"
      label={`${label} ${filterNum}`}
      color="neutral"
      sx={{ backgroundColor: "hubColors.white" }}
      {...(icon && { icon })}
      iconProps={{ size: 10 }}
      {...buttonProps}
    />
  );
  return (
    <HubContextMenu
      id={`filter-${id}`}
      menuButton={MenuButton}
      IconButtonProps={{ sx: { padding: 0, minHeight: 0 } }}
    >
      {MenuOptions}
    </HubContextMenu>
  );
};

export default HubFilterMenu;
